// Lightweight client for Auth0 client credentials + Notification SMS API.
// NOTE: Do not expose real client_secret in committed code. Set env vars at runtime.
import { notificationConfig } from '../config/notification';
import { getSmsToken, primeSmsToken, clearToken } from './machineTokenService';
import { getUserAuthToken } from '../utils/authToken';

export { primeSmsToken } from './machineTokenService';

const SMS_BASE_FALLBACK = 'https://annadaneshwar-notification-service.azurewebsites.net';

const parsePayload = async (res, fallbackMessage) => {
  const data = await res.json().catch(async () => {
    const text = await res.text().catch(() => null);
    if (text) return { message: text };
    return null;
  });

  return {
    message: data?.message || fallbackMessage,
    data,
  };
};

const successFromOpaque = (fallbackMessage) => ({
  message: `${fallbackMessage} (response hidden by CORS).`,
  data: null,
});

const attemptFetch = async (fn, setError) => {
  try {
    return await fn();
  } catch (err) {
    setError(err.message);
    return null;
  }
};

async function performSmsRequest(path, queryParams, successMessage, errorLabel, token) {
  const base = notificationConfig.smsApiBase || SMS_BASE_FALLBACK;
  const sanitizedBase = base.replace(/\/$/, '');
  const search = new URLSearchParams(queryParams);
  const url = `${sanitizedBase}${path}?${search.toString()}`;

  const authed = () => fetch(url, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'omit',
    headers: {
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  const withTokenQuery = (noCors = false) => {
    const fallbackUrl = `${url}&access_token=${encodeURIComponent(token)}`;
    return fetch(fallbackUrl, {
      method: 'POST',
      mode: noCors ? 'no-cors' : 'cors',
      cache: 'no-cache',
      credentials: 'omit',
    });
  };

  const fallback = () => withTokenQuery(false);
  const fallbackNoCors = () => withTokenQuery(true);

  let lastErrorText = '';
  let lastStatus;

  const setLastErrorText = (msg) => {
    if (msg) {
      lastErrorText = msg;
    }
  };

  const attempts = [authed, fallback];

  for (let i = 0; i < attempts.length; i += 1) {
    const res = await attemptFetch(attempts[i], setLastErrorText);
    if (!res) {
      continue;
    }

    if (res.type === 'opaque') {
      return { ok: true, response: successFromOpaque(successMessage) };
    }

    if (res.ok) {
      const parsed = await parsePayload(res, successMessage);
      return { ok: true, response: parsed };
    }

    lastStatus = res.status;
    lastErrorText = await res.text().catch(() => '') ?? '';

    if (res.status === 403 && i === attempts.length - 1) {
      const opaque = await attemptFetch(fallbackNoCors, setLastErrorText);
      if (opaque?.type === 'opaque') {
        return { ok: true, response: successFromOpaque(successMessage) };
      }
    }
  }

  const hint = lastErrorText || 'Possible CORS/preflight rejection or missing API permissions.';
  return {
    ok: false,
    status: typeof lastStatus === 'number' ? lastStatus : null,
    message: `${errorLabel} failed (${lastStatus ?? 'network error'}): ${hint}`,
  };
}

export async function sendSms(to, message) {
  if (!to || !message) throw new Error('to and message are required');
  return invokeSmsEndpoint('/public/api/sms/send', { to, message }, 'SMS delivered successfully', 'Send SMS');
}

export async function scheduleSms(to, message, dateTime, timezone) {
  if (!to || !message || !dateTime || !timezone) throw new Error('to, message, dateTime, and timezone are required');
  return invokeSmsEndpoint(
    '/public/api/sms/schedule',
    { to, message, dateTime, timezone },
    'SMS scheduled successfully',
    'Schedule SMS',
  );
}

async function invokeSmsEndpoint(path, queryParams, successMessage, errorLabel) {
  let hasRetriedWithFreshToken = false;

  while (true) {
    const userToken = getUserAuthToken();
    const token = userToken?.token || await getSmsToken();
    const outcome = await performSmsRequest(path, queryParams, successMessage, errorLabel, token);
    if (outcome.ok) {
      return outcome.response;
    }

    if (!hasRetriedWithFreshToken && (outcome.status === 401 || outcome.status === 403)) {
      clearToken('sms');
      hasRetriedWithFreshToken = true;
      continue;
    }

    throw new Error(outcome.message);
  }
}

const notificationApi = {
  sendSms,
  primeSmsToken,
  scheduleSms,
};

export default notificationApi;
